/**
 * Inline source citations for Chat with your docs (DEV-6130). The agent's
 * final answer carries `[N]` markers and a separate `citations` list
 * ({ number, documentName, documentUrl, excerpt, pageNumber, searchTerm });
 * these helpers turn the markers into clickable pills and shape the quick-view
 * text. Mirrors the web app's chat-component citation handling.
 */

export const CITATION_PILL_CLASS = "addy-citation-pill";

const CITATION_CLUSTER_REGEX = /\[[\d[\]]*\d[\d[\]]*\]/g;
const CITATION_MARKER_REGEX = /\[(\d+)(?::"[^"]*")?\]/g;
const SKIPPED_PARENT_SELECTOR = "a, code, pre, button";
const MAX_EXCERPT_LENGTH = 220;
const EXCERPT_CONTEXT = 60;

/**
 * Rebuilds malformed marker clusters from the backend injector (`[5[6]]`,
 * `[5][5]`) as separate, de-duplicated markers so every citation renders as
 * its own pill. Quoted markers and ordinary bracketed text are left alone.
 */
export const normalizeCitationMarkers = (text = "") =>
  text.replace(CITATION_CLUSTER_REGEX, (cluster) => {
    const numbers = cluster.match(/\d+/g) || [];

    return [...new Set(numbers)].map((number) => `[${number}]`).join("");
  });

/** Merges citation lists by number; a later entry for the same number wins. */
export const mergeCitations = (currentCitations = [], incomingCitations = []) => {
  const byNumber = new Map(currentCitations.map((citation) => [citation.number, citation]));
  incomingCitations
    .filter((citation) => Number.isFinite(citation?.number))
    .forEach((citation) => byNumber.set(citation.number, citation));

  return [...byNumber.values()].sort((first, second) => first.number - second.number);
};

const createCitationPill = (doc, number) => {
  const pill = doc.createElement("button");
  pill.type = "button";
  pill.className = CITATION_PILL_CLASS;
  pill.dataset.citationNumber = String(number);
  pill.setAttribute("aria-label", `View source citation ${number}`);
  // Copy leaves the pills out, like the answer's other controls.
  pill.setAttribute("data-copy-skip", "");
  pill.textContent = String(number);

  return pill;
};

/** Removes cited `[N]` markers from answer text, e.g. before exporting it. */
export const stripCitationMarkers = (text = "", citations = []) => {
  const citedNumbers = new Set(citations.map(({ number }) => number));
  if (citedNumbers.size === 0) {
    return text;
  }

  return normalizeCitationMarkers(text)
    .replace(CITATION_MARKER_REGEX, (marker, number) =>
      citedNumbers.has(Number(number)) ? "" : marker
    )
    .replace(/[ \t]+([.,;:!?])/g, "$1")
    .replace(/[ \t]+$/gm, "");
};

/**
 * Replaces `[N]` markers inside rendered answer HTML with citation pills, for
 * numbers that have a citation. Text inside links, code and buttons is skipped.
 *
 * @param {HTMLElement} root - Container holding the rendered (sanitized) answer
 * @param {Array<{ number: number }>} citations
 */
export const insertCitationPills = (root, citations = []) => {
  const citedNumbers = new Set(citations.map(({ number }) => number));
  if (!root || citedNumbers.size === 0) {
    return;
  }

  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    textNodes.push(node);
  }

  textNodes
    .filter((node) => !node.parentElement?.closest(SKIPPED_PARENT_SELECTOR))
    .forEach((node) => {
      const text = node.textContent || "";
      const markers = [...text.matchAll(CITATION_MARKER_REGEX)].filter(([, number]) =>
        citedNumbers.has(Number(number))
      );
      if (markers.length === 0) {
        return;
      }

      const fragment = doc.createDocumentFragment();
      let lastIndex = 0;
      markers.forEach((marker) => {
        const [fullMarker, number] = marker;
        fragment.append(text.slice(lastIndex, marker.index));
        fragment.append(createCitationPill(doc, Number(number)));
        lastIndex = marker.index + fullMarker.length;
      });
      fragment.append(text.slice(lastIndex));
      node.replaceWith(fragment);
    });
};

/**
 * Strips markdown from a highlight term so the PDF viewer searches for the
 * plain value ("$500,000", not "**$500,000**").
 */
export const stripMarkdown = (value = "") =>
  value
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/_(.+?)_/g, "$1")
    .replace(/`(.+?)`/g, "$1")
    .replace(/[*_`]/g, "")
    .replace(/^[#>\-\s]+/, "")
    .replace(/\s+/g, " ")
    .trim();

/** Keeps the quick view on the cited value instead of a page of surrounding text. */
export const formatCitationExcerpt = (excerpt = "", searchTerm = "") => {
  const value = (excerpt || "").trim();
  const target = stripMarkdown(searchTerm || "");
  if (!value) {
    return target;
  }
  if (value.length <= MAX_EXCERPT_LENGTH) {
    return value;
  }

  const targetIndex = target ? value.toLowerCase().indexOf(target.toLowerCase()) : -1;
  if (targetIndex === -1) {
    return `${value.slice(0, MAX_EXCERPT_LENGTH).trim()}…`;
  }

  const start = Math.max(0, targetIndex - EXCERPT_CONTEXT);
  const end = Math.min(value.length, targetIndex + target.length + EXCERPT_CONTEXT);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < value.length ? "…" : "";

  return `${prefix}${value.slice(start, end).trim()}${suffix}`;
};

/** PDFs open in the in-app viewer at the cited page; other files open as-is. */
export const isPdfCitation = ({ documentUrl = "", documentName = "" } = {}) => {
  const urlPath = documentUrl.split("?")[0].split("#")[0];

  return /\.pdf$/i.test(urlPath) || /\.pdf$/i.test(documentName.trim());
};

const TRUSTED_HOST_APP_ORIGINS = [
  "https://app.addy.so",
  "https://app.addy.com",
  "https://devmail.addy.so",
  "https://devmail.addy.com",
  "http://localhost:3000",
];

/**
 * The embedding app's origin when Ask Addy sits inside a known Addy app, so
 * "open this cited document" can be handed to that app's viewer. Empty
 * anywhere else (the Chrome extension on Gmail, the help-center widget), where
 * the document opens in a new tab instead and links never go to an unknown page.
 */
export const getCitationTargetOrigin = (
  location = window.location,
  referrer = document.referrer
) => {
  const ancestorOrigin = location?.ancestorOrigins?.[0] || "";
  const referrerOrigin = (() => {
    try {
      return referrer ? new URL(referrer).origin : "";
    } catch {
      return "";
    }
  })();
  const parentOrigin = ancestorOrigin || referrerOrigin;

  return TRUSTED_HOST_APP_ORIGINS.includes(parentOrigin) ? parentOrigin : "";
};

/** Payload for the host app's citation PDF viewer. */
export const buildCitationViewerRequest = (citation = {}) => {
  const { documentUrl = "", pageNumber, searchTerm = "", excerpt = "", documentName = "" } = citation;

  return {
    url: documentUrl,
    page: Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : undefined,
    searchTerm: stripMarkdown(searchTerm || excerpt),
    documentName,
  };
};
